import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoData from '@/widget/NoData';
import LimitOrderCard from './LimitOrderCard';
import MarketOrderCard from './MarketOrderCard';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Filter } from '@/widget/Icon';
import { useTheme } from '@/components/ThemeProvider';
import { ITokenOrder } from '@/request/token/token.response.interface';
import { SOL_TOKEN_ADDRESS } from '@/config';
import { limitedOrder, useTokenOrder } from '@/page/trade/hook/useTokenOrder';
import { ORDER_TYPE } from '@/interface';
import { useBoomWallet } from '@boomeme/wallet-sdk';

export default () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { chain } = useBoomWallet();
  const [orderType, setOrderType] = useState<ORDER_TYPE>('market');
  const [tradeType, setTradeType] = useState<'buy' | 'sell' | 'all'>('all');
  const { marketOrderList, limitedOrderList } = useTokenOrder();
  const [marketRenderList, setMarketRenderList] = useState<ITokenOrder[]>([]);
  const [limitRenderList, setLimitRenderList] = useState<limitedOrder[]>([]);

  useEffect(() => {
    if (orderType == 'market') {
      if (tradeType == 'all') {
        setMarketRenderList(marketOrderList);
      } else {
        setMarketRenderList(marketOrderList.filter((item) => item.type == tradeType));
      }
    } else {
      if (tradeType == 'all') {
        setLimitRenderList(limitedOrderList);
      } else {
        setLimitRenderList(
          limitedOrderList.filter((item) => {
            const type = item.inputMint.toLocaleLowerCase() === SOL_TOKEN_ADDRESS.toLocaleLowerCase() ? 'buy' : 'sell';
            return type == tradeType;
          }),
        );
      }
    }
  }, [orderType, tradeType, marketOrderList, limitedOrderList]);

  return (
    <div className="flex flex-col gap-y-[12px]">
      <Tabs value={orderType} onValueChange={(e) => setOrderType(e as ORDER_TYPE)}>
        <div className="flex justify-between items-center">
          <TabsList className="rounded-[8px] h-[40px] p-[4px] my-[16px]">
            <TabsTrigger className="w-[76px] h-[32px] rounded-[6px] font-[400]" value="market">
              {t('Trade.market')}
            </TabsTrigger>
            {chain == 'solana' && (
              <TabsTrigger className="w-[76px] h-[32px] rounded-[8px] font-[400]" value="limit">
                {t('Trade.limit')}
              </TabsTrigger>
            )}
          </TabsList>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-[36px] h-[36px] p-0 flex justify-center items-center">
                <Filter className="w-[18px] h-[18px]" theme={theme} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuCheckboxItem checked={tradeType === 'all'} onCheckedChange={() => setTradeType('all')}>
                <span>{t('Account.all')}</span>
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={tradeType === 'buy'} onCheckedChange={() => setTradeType('buy')}>
                <span>{t('Account.buy')}</span>
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={tradeType === 'sell'} onCheckedChange={() => setTradeType('sell')}>
                <span>{t('Account.sell')}</span>
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <TabsContent value="market" className="mt-0">
          <div className="flex flex-col gap-y-[16px]">
            {marketRenderList.length > 0 ? marketRenderList.map((item, index) => <MarketOrderCard order={item} key={index} />) : <NoData />}
          </div>
        </TabsContent>
        {chain == 'solana' && (
          <TabsContent value="limit" className="mt-0">
            <div className="flex flex-col gap-y-[16px]">
              {limitRenderList.length > 0 ? limitRenderList.map((item, index) => <LimitOrderCard key={index} order={item} />) : <NoData />}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
